import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { resolveCustomerReturnTo, resolveCustomerPostAuthDestination } from '../../auth/experience';
import { AuthChrome } from '../../components/brand/AuthChrome';
import { api } from '../../api/client';
import type { Session } from '../../types';

function oauthErrorMessage(
  code: string | null,
  t: (key: string, fallback?: string) => string
): string | null {
  if (!code) return null;
  switch (code) {
    case 'cancelled':
      return t('auth.googleCancelled', 'Google sign-in was cancelled.');
    case 'not_configured':
      return t(
        'auth.googleNotConfigured',
        'Google sign-in is unavailable. Ask an administrator to configure Google OAuth.'
      );
    case 'invalid_state':
      return t('auth.googleInvalidState', 'Google sign-in expired. Please try again.');
    case 'unverified_email':
      return t('auth.googleUnverified', 'Your Google email must be verified to continue.');
    case 'suspended':
      return t('auth.suspended', 'This account is suspended.');
    case 'linking_conflict':
      return t(
        'auth.googleLinkConflict',
        'This email is already linked to a different Google account.'
      );
    case 'registration_required':
      return t(
        'auth.googleRegistrationRequired',
        'Google sign-up is temporarily unavailable. Contact Sales.'
      );
    case 'admin_portal_required':
      return t(
        'auth.adminPortalRequired',
        'Platform admins sign in at the Admin Platform — not with Google customer sign-in.'
      );
    case 'developer_disabled':
      return t('auth.developerDisabled', 'Developer accounts are not active in this release.');
    default:
      return t('auth.googleFailed', 'Google sign-in failed. Please try again.');
  }
}

/** Module-scoped so StrictMode remount / unstable effect deps cannot double-POST the one-time ticket. */
const googleCompleteInflight = new Set<string>();

function destinationAfterGoogleSession(
  session: Session & { returnTo?: string; needsOnboarding?: boolean },
  fallbackReturnTo: string
): string {
  return resolveCustomerPostAuthDestination({
    returnTo: session.returnTo || fallbackReturnTo,
    needsOnboarding: session.needsOnboarding || session.user.needsOnboarding,
    isAdmin: session.user.role === 'platform_admin',
  });
}

/** Customer experience — Google Sign-In only. Resumes the original account action via `next`. */
export const LoginPage: React.FC = () => {
  const { completeGoogleSession, isAuthenticated, isAdmin, user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromState = (location.state as { from?: string; reason?: string } | null)?.from;
  const reason = (location.state as { reason?: string } | null)?.reason;
  const nextParam = params.get('next') || fromState || '/customer';
  const returnTo = resolveCustomerReturnTo(nextParam, '/customer');

  const [error, setError] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState<boolean | null>(null);
  const [oauthHandoffTo, setOauthHandoffTo] = useState<string | null>(null);
  const returnToRef = useRef(returnTo);
  const completeRef = useRef(completeGoogleSession);
  const tRef = useRef(t);
  returnToRef.current = returnTo;
  completeRef.current = completeGoogleSession;
  tRef.current = t;

  useEffect(() => {
    const oauthError = params.get('oauth_error');
    if (oauthError) {
      setError(oauthErrorMessage(oauthError, tRef.current));
      if (oauthError === 'admin_portal_required') {
        navigate('/admin/login', { replace: true });
      }
    }
  }, [params, navigate]);

  useEffect(() => {
    let cancelled = false;
    api
      .googleAuthStatus()
      .then((status) => {
        if (!cancelled) setGoogleConfigured(status.configured);
      })
      .catch(() => {
        if (!cancelled) setGoogleConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const googleFlag = params.get('google');
  const ticketCode = params.get('code');

  useEffect(() => {
    if (googleFlag !== '1' || !ticketCode) return;
    // One HTTP consume per ticket for the whole tab — survives effect re-runs and StrictMode remount.
    if (googleCompleteInflight.has(ticketCode)) return;
    googleCompleteInflight.add(ticketCode);

    let cancelled = false;
    setGoogleBusy(true);
    setError(null);

    completeRef
      .current(ticketCode)
      .then((session) => {
        const dest = destinationAfterGoogleSession(session, returnToRef.current);
        // Ticket is already consumed — always hand off, even if this effect instance was cleaned up.
        setOauthHandoffTo(dest);
      })
      .catch((err) => {
        googleCompleteInflight.delete(ticketCode);
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : tRef.current('auth.googleFailed', 'Google sign-in failed.')
        );
        setGoogleBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [googleFlag, ticketCode]);

  if (oauthHandoffTo) {
    return <Navigate to={oauthHandoffTo} replace />;
  }

  if (isAuthenticated && reason !== 'forbidden' && googleFlag !== '1') {
    if (isAdmin) return <Navigate to="/admin" replace />;
    return (
      <Navigate
        to={resolveCustomerPostAuthDestination({
          returnTo,
          needsOnboarding: Boolean(user?.needsOnboarding),
        })}
        replace
      />
    );
  }

  // Completing Google — do not keep AuthChrome (atmosphere + glass) mounted under the next route.
  if (googleFlag === '1' && googleBusy) {
    return (
      <div className="bc-auth-shell bc-auth-shell--v4" role="status" aria-live="polite">
        <p className="text-sm text-zinc-400">{t('auth.googleLoading', 'Continuing with Google…')}</p>
      </div>
    );
  }

  const startGoogle = () => {
    setError(null);
    if (googleConfigured === false) {
      setError(
        t(
          'auth.googleNotConfigured',
          'Google sign-in is unavailable. Ask an administrator to configure Google OAuth.'
        )
      );
      return;
    }
    setGoogleBusy(true);
    window.location.assign(api.googleAuthorizeUrl(language, returnTo));
  };

  return (
    <AuthChrome
      title={t('auth.customerSignInTitle', 'Continue with Google')}
      subtitle={t(
        'auth.customerSignInSubtitle',
        'Customer portal access. Browse freely — sign in only for trials, subscriptions, purchase, launch, or account.'
      )}
      footer={
        <div className="flex flex-wrap justify-between text-xs gap-3 pt-1 border-t border-white/10">
          <Link to="/" className="text-zinc-400 hover:text-paper transition-colors">
            {t('auth.backHome', 'Back home')}
          </Link>
          <Link to="/admin/login" className="text-zinc-500 hover:text-zinc-300 transition-colors">
            {t('auth.adminSignInLink', 'Admin sign-in')}
          </Link>
        </div>
      }
    >
      {reason === 'forbidden' && (
        <div
          role="status"
          className="text-xs p-3 rounded-md bg-amber-500/10 text-amber-200 border border-amber-500/20"
        >
          {t('auth.forbidden', 'Your account does not have access to that portal.')}
        </div>
      )}

      {error && (
        <p
          role="alert"
          aria-live="assertive"
          className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}

      <div className="space-y-3">
        {googleConfigured === true && (
          <button
            type="button"
            disabled={googleBusy}
            onClick={startGoogle}
            className="bc-btn-primary w-full !py-3 !min-h-11"
          >
            {googleBusy
              ? t('auth.googleLoading', 'Continuing with Google…')
              : t('auth.continueGoogle', 'Continue with Google')}
          </button>
        )}

        {googleConfigured === false && (
          <p className="text-xs text-zinc-400 bg-black/25 border border-white/12 rounded-md px-3 py-2">
            {t(
              'auth.googleUnavailable',
              'Google sign-in is unavailable until it is configured for this environment.'
            )}
          </p>
        )}

        {googleConfigured === null && (
          <p className="text-xs text-zinc-400">{t('common.loading')}</p>
        )}
      </div>

      <p className="text-[11px] text-zinc-400 border-t border-white/12 pt-5">
        {t(
          'auth.googleCustomerOnly',
          'Customers use Google Sign-In only. After you sign in, BeCoder continues the action you started.'
        )}
      </p>
    </AuthChrome>
  );
};
